import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import classNames from "classnames";
import HoverGallery from "@components/Features/HoverGallery/HoverGallery";
import HearBtnIcon from "@components/Svg/HearBtnIcon";
import { numberWithSpaces } from "@src/utils/formatUtils";
import {
    useAddToFavoritesMutation,
    useGetFavoritesOffersQuery,
    useRemoveFromFavoritesMutation
} from "@app/store/offersApi";
import { AppState } from "@app/store/store";
import { OfferIcon } from "@features/catalog";
import { CompareToggle } from "@features/compare/CompareToggle";
import { ProductPriceUsd } from "@features/product/product-price-usd";
import { useGetCompareOffersQuery } from "@entities/compare";
import Car from "@shared/lib/interfaces/car.interface";
import { RouterConfig } from "@shared/lib/routerConfig";
import { getCurrencySymbol, toRelativeTime } from "@shared/lib/utils";
import { SOLD_STATUS_ID } from "../vertical-catalog-card/vertical-catalog-card";
import { CatalogCardProps } from "./types";
import classes from "./catalog-card.module.scss";

const CatalogCard = ({
    showFavs = true,
    fullWidth,
    forcedHover,
    hideSource = false,
    ...props
}: CatalogCardProps) => {
    const offer = props.data;
    const address = [props.data.city, props.data.address].filter(Boolean).join(", ");
    let offerTitle = offer.mark && offer.model ? `${offer.mark} ${offer.model}` : offer.title;
    if (offer.generationName) {
        offerTitle = `${offerTitle} (${offer.generationName})`;
    }

    const [isArchive, setIsArchive] = useState(false);
    const isAuthorized = useSelector((state: AppState) => state.auth.accessToken);
    const hasToken = typeof isAuthorized === "string" && isAuthorized.length > 0;
    const priceInCurrency = `${numberWithSpaces(props.data.price.toString())} ${getCurrencySymbol(props.data.sourceId)}`;
    const showSource = !hideSource;

    // TODO: handle errors
    const { data } = useGetFavoritesOffersQuery(
        {},
        {
            skip: !hasToken
        }
    );
    const { data: compareData } = useGetCompareOffersQuery(undefined, {
        skip: !hasToken
    });

    const [isFavorite, setIsFavorite] = useState(false);
    const [_isComparison, setIsComparison] = useState(false);

    const propsString = `${offer.displacement ? `${offer.displacement} л / ` : ""}${offer.horsePower ? `${offer.horsePower} л.с. / ` : ""}${offer.prepEngineType ? `${offer.prepEngineType}` : ""}`;

    useEffect(() => {
        if (data) {
            setIsFavorite(data.some((offer: any) => offer.id === props.data.id));
            setIsArchive(props.data.statusId === SOLD_STATUS_ID);
        }
        if (compareData) {
            setIsComparison(compareData.offers.some((item: Car) => item.id === offer.id));
        }
    }, [data, compareData]);

    const [addToFavorites] = useAddToFavoritesMutation();
    const [removeFromFavorite] = useRemoveFromFavoritesMutation();
    return (
        <div
            className={classNames(classes.Root, {
                [classes.FullWidth]: fullWidth
            })}
        >
            {isArchive && <div className={classes.DisabledText}>Снято с продажи</div>}
            <div
                className={isArchive ? classes.Disabled : ""}
                onClick={(e) => {
                    if (isArchive) {
                        e.preventDefault();
                        e.stopPropagation();
                    }
                }}
            >
                <div
                    className={classNames(classes.CatalogCard, {
                        [classes.ForcedHover]: forcedHover
                    })}
                    onMouseEnter={() => {
                        if (props.setIsHovered) {
                            props.setIsHovered(true);
                        }
                    }}
                    onMouseLeave={() => {
                        if (props.setIsHovered) {
                            props.setIsHovered(false);
                        }
                    }}
                >
                    <div className={classes.CatalogCardContainer}>
                        <div className={classes.ImgContainer}>
                            <a
                                target="_blank"
                                rel="noopener noreferrer"
                                href={`${RouterConfig.PRODUCT}/${offer.id}`}
                            >
                                <HoverGallery
                                    offer={offer}
                                    pics={offer.imagesList}
                                    style={{
                                        width: 300,
                                        height: 200,
                                        borderRadius: 12,
                                        overflow: "hidden"
                                    }}
                                />
                            </a>
                        </div>

                        <div className={classes.Info}>
                            <div className={classes.TitleContainer}>
                                <a
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href={`${RouterConfig.PRODUCT}/${offer.id}`}
                                >
                                    {offerTitle}
                                </a>
                            </div>

                            <div className={classes.Tags}>
                                <div>{offer.year} г</div>
                                {offer.mileage && <div>{numberWithSpaces(offer.mileage)} км</div>}
                            </div>

                            <div className={classes.Props}>{propsString}</div>

                            <div className={classes.MoreProps}>
                                <div className={classes.MorePropLabels}>
                                    <label>{offer.prepTransmission}</label>
                                    <label>{offer.prepDriveType} привод</label>
                                </div>

                                <div className={classes.MorePropVals}>
                                    <div className={classes.MorePropsVal}>{offer.prepBodyType}</div>
                                    <div className={classes.MorePropsVal}>{offer.prepColor}</div>
                                </div>
                            </div>

                            <div className={classes.Location}>
                                <svg
                                    width="8"
                                    height="8"
                                    viewBox="0 0 8 8"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <circle cx="4" cy="4" r="4" fill="#3B85F7" />
                                </svg>
                                {address}
                            </div>
                        </div>

                        {showSource && (
                            <div className={classes.Source}>
                                {props.infoBlockContent && props.infoBlockContent}
                                {!props.infoBlockContent && (
                                    <>
                                        <OfferIcon
                                            className={classes.Source_icon}
                                            sourceId={offer.sourceId}
                                        />
                                        {toRelativeTime(offer.createdAt)}
                                    </>
                                )}
                            </div>
                        )}

                        <div className={classes.Price}>
                            <span className={classes.Price__source}>{priceInCurrency}</span>
                            <ProductPriceUsd
                                className={classes.Price__usd}
                                price={offer.price}
                                priceUsd={offer.priceUsd}
                                sourceId={offer.sourceId}
                            />

                            {props.buttonsBlockContent && (
                                <div className={classes.buttonsBlockContent}>
                                    {props.buttonsBlockContent}
                                </div>
                            )}
                        </div>

                        <div className={classes.Buttons}>
                            <CompareToggle
                                className={classes.CompareOffer}
                                classNameIcon={classes.CompareOffer__icon}
                                classNameIconActive={classNames(
                                    classes["CompareOffer__icon--active"]
                                )}
                                offerId={offer.id}
                                isAuth={hasToken}
                            />
                            {showFavs && (
                                <a
                                    className={classes.FavoriteOffer}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (isFavorite) {
                                            removeFromFavorite({
                                                id: offer.id
                                            });
                                            setIsFavorite(!isFavorite);
                                        } else {
                                            addToFavorites({
                                                id: offer.id
                                            });
                                            setIsFavorite(!isFavorite);
                                        }
                                    }}
                                    href=""
                                >
                                    <HearBtnIcon isSelected={isFavorite} />
                                </a>
                            )}
                        </div>

                        {props.button ? (
                            <div className={classes.ButtonCont}>{props.button}</div>
                        ) : null}
                    </div>

                    <div
                        className={classNames(classes.Divider, {
                            [classes.HideDivider]: !props.showDivider
                        })}
                    ></div>
                </div>
            </div>
        </div>
    );
};
export default CatalogCard;